/**
 * FeedItem Component
 * Displays individual social activity items
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Heart, MapPin, Globe, User, FileText } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { FeedItem as FeedItemType } from '../../types/social.types';
import { ProfileAvatar } from '../../shared/components/ProfileAvatar';
import { LikeButton } from '../Social/LikeButton';
import './FeedItem.css';

interface FeedItemProps {
  item: FeedItemType;
}

export const FeedItem: React.FC<FeedItemProps> = ({ item }) => {
  const navigate = useNavigate();

  const getActivityIcon = () => {
    switch (item.activityType) {
      case 'FOLLOW':
        return (
          <UserPlus
            size={20}
            className='feed-item-activity-icon follow'
          />
        );
      case 'LIKE':
        return (
          <Heart
            size={20}
            className='feed-item-activity-icon like'
          />
        );
      case 'CREATE_TRIP':
      case 'PUBLISH_TRIP':
        return (
          <Globe
            size={20}
            className='feed-item-activity-icon trip'
          />
        );
      case 'ADD_DESTINATION':
        return (
          <MapPin
            size={20}
            className='feed-item-activity-icon destination'
          />
        );
      case 'UPDATE_PROFILE':
        return (
          <User
            size={20}
            className='feed-item-activity-icon profile'
          />
        );
      default:
        return (
          <FileText
            size={20}
            className='feed-item-activity-icon default'
          />
        );
    }
  };

  const getActivityText = (): { action: string; target?: string } => {
    switch (item.activityType) {
      case 'FOLLOW':
        return {
          action: 'started following',
          target: item.metadata?.targetUsername || 'someone'
        };
      case 'LIKE':
        return {
          action: 'liked',
          target: item.metadata?.tripTitle || 'a trip'
        };
      case 'CREATE_TRIP':
        return {
          action: 'created a new trip',
          target: item.metadata?.tripTitle
        };
      case 'PUBLISH_TRIP':
        return {
          action: 'published',
          target: item.metadata?.tripTitle
        };
      case 'ADD_DESTINATION':
        return {
          action: 'added',
          target: item.metadata?.destinationName
            ? `${item.metadata.destinationName} to their trip`
            : 'a new destination'
        };
      case 'UPDATE_PROFILE':
        return {
          action: 'updated their profile'
        };
      default:
        return {
          action: 'performed an action'
        };
    }
  };

  const handleClick = () => {
    // Navigate based on entity type
    switch (item.entityType) {
      case 'TRIP':
        navigate(`/trips/${item.entityId}`);
        break;
      case 'USER':
      case 'PROFILE':
        navigate(`/profile/${item.entityId}`);
        break;
      case 'ITINERARY':
        navigate(`/itineraries/${item.entityId}`);
        break;
      default:
        break;
    }
  };

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.user?._id) {
      navigate(`/profile/${item.user._id}`);
    }
  };

  const getTimeAgo = (): string => {
    try {
      return formatDistanceToNow(new Date(item.createdAt), { addSuffix: true });
    } catch {
      return '';
    }
  };

  const activityText = getActivityText();
  const showLikeButton =
    item.entityType === 'TRIP' && item.activityType !== 'LIKE';

  return (
    <div
      className='feed-item'
      onClick={handleClick}
    >
      <div className='feed-item-icon-wrapper'>{getActivityIcon()}</div>

      <div className='feed-item-content'>
        <div className='feed-item-header'>
          <div
            className='feed-item-user'
            onClick={handleUserClick}
          >
            <ProfileAvatar
              src={item.user?.profileImage}
              username={item.user?.username || 'User'}
              size='sm'
            />
            <span className='feed-item-username'>
              {item.user?.username || 'User'}
            </span>
          </div>
          <span className='feed-item-time'>{getTimeAgo()}</span>
        </div>

        <div className='feed-item-activity'>
          <span className='feed-item-action'>{activityText.action}</span>
          {activityText.target && (
            <span className='feed-item-target'>{activityText.target}</span>
          )}
        </div>

        {item.metadata?.description && (
          <div className='feed-item-description'>
            {item.metadata.description}
          </div>
        )}

        {showLikeButton && (
          <div className='feed-item-actions'>
            <LikeButton
              entityType='TRIP'
              entityId={item.entityId}
              initialIsLiked={false}
              initialLikeCount={item.metadata?.likesCount || 0}
              size='sm'
              showCount={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};
